import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Mail, Send, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { NotificationsCard } from "./NotificationsCard";

const initial = {
    host: "",
    port: 465,
    security: "ssl",
    username: "",
    password: "",
    from_name: "Synara Alvarenga",
    from_email: "",
    reply_to: "",
    enabled: false,
};

export const SettingsAdmin = () => {
    const [form, setForm] = useState(initial);
    const [passwordSet, setPasswordSet] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testTo, setTestTo] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings/smtp");
            setForm((prev) => ({
                ...prev,
                host: data.host || "",
                port: data.port || 465,
                security: data.security || "ssl",
                username: data.username || "",
                password: "",
                from_name: data.from_name || "Synara Alvarenga",
                from_email: data.from_email || "",
                reply_to: data.reply_to || "",
                enabled: !!data.enabled,
            }));
            setPasswordSet(!!data.password_set);
        } catch (e) {
            toast.error("Erro a carregar definições SMTP");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleChange = (k) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((p) => ({ ...p, [k]: value }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, port: Number(form.port) || 465 };
            // don't send empty password — preserves existing one
            if (!payload.password) delete payload.password;
            const { data } = await api.put("/admin/settings/smtp", payload);
            setPasswordSet(!!data.password_set);
            setForm((p) => ({ ...p, password: "" }));
            toast.success("Definições guardadas");
        } catch (e) {
            toast.error("Erro a guardar definições");
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        if (!testTo) {
            toast.error("Indica um email de destino para o teste.");
            return;
        }
        setTesting(true);
        try {
            await api.post("/admin/settings/smtp/test", { to: testTo });
            toast.success(`Email de teste enviado para ${testTo} ✨`);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Falha no envio. Verifica as credenciais.";
            toast.error(msg);
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-stone-500 py-10">
                <Loader2 className="h-4 w-4 animate-spin" /> A carregar...
            </div>
        );
    }

    const configured = form.host && form.username && (passwordSet || form.password);

    return (
        <div className="space-y-8" data-testid="settings-admin">
            {/* Status banner */}
            <div className={`rounded-xl ring-1 p-4 flex items-start gap-3 ${
                form.enabled && configured
                    ? "bg-emerald-50 ring-emerald-200 text-emerald-900"
                    : "bg-amber-50 ring-amber-200 text-amber-900"
            }`}>
                {form.enabled && configured
                    ? <CheckCircle2 className="h-5 w-5 mt-0.5" />
                    : <AlertCircle className="h-5 w-5 mt-0.5" />
                }
                <div className="text-sm leading-relaxed">
                    {form.enabled && configured ? (
                        <>SMTP <strong>ativo</strong>. Os emails de boas-vindas e propostas serão enviados a partir de <strong>{form.from_email || form.username}</strong>.</>
                    ) : (
                        <>SMTP ainda não está ativo. Preenche o host, porta, utilizador, password e <strong>ativa o interruptor</strong> em baixo. Depois carrega em <em>Guardar</em>.</>
                    )}
                </div>
            </div>

            {/* SMTP form */}
            <div className="bg-white ring-1 ring-stone-200 rounded-2xl p-6 md:p-8 space-y-6">
                <header className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-gold-soft text-gold-deep ring-1 ring-gold/30">
                        <Mail className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="font-serif text-2xl text-stone-900">Configuração SMTP</h2>
                        <p className="text-sm text-stone-500">Servidor de envio de email do teu domínio.</p>
                    </div>
                </header>

                <div className="grid md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                        <Label>Servidor SMTP (host)</Label>
                        <Input
                            placeholder="ex: smtppro.zoho.eu, mail.chefsynara.com"
                            value={form.host}
                            onChange={handleChange("host")}
                            data-testid="smtp-host"
                        />
                    </div>

                    <div>
                        <Label>Porta</Label>
                        <Input
                            type="number"
                            placeholder="465"
                            value={form.port}
                            onChange={handleChange("port")}
                            data-testid="smtp-port"
                        />
                    </div>

                    <div>
                        <Label>Segurança</Label>
                        <Select value={form.security} onValueChange={handleChange("security")}>
                            <SelectTrigger data-testid="smtp-security"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ssl">SSL/TLS (465) — recomendado</SelectItem>
                                <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                                <SelectItem value="none">Sem encriptação</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label>Utilizador</Label>
                        <Input
                            placeholder="ola@chefsynara.com"
                            value={form.username}
                            onChange={handleChange("username")}
                            data-testid="smtp-username"
                        />
                    </div>

                    <div>
                        <Label>Password {passwordSet && !form.password ? <span className="text-xs text-stone-400 ml-1">(guardada — deixa vazio para manter)</span> : null}</Label>
                        <div className="relative">
                            <Input
                                type={showPwd ? "text" : "password"}
                                placeholder={passwordSet ? "••••••••" : "Password do email"}
                                value={form.password}
                                onChange={handleChange("password")}
                                data-testid="smtp-password"
                                className="pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd(!showPwd)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                            >
                                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <Label>Nome remetente</Label>
                        <Input
                            value={form.from_name}
                            onChange={handleChange("from_name")}
                            data-testid="smtp-from-name"
                        />
                    </div>

                    <div>
                        <Label>Email remetente (From)</Label>
                        <Input
                            placeholder="ola@chefsynara.com"
                            value={form.from_email}
                            onChange={handleChange("from_email")}
                            data-testid="smtp-from-email"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <Label>Reply-To (opcional)</Label>
                        <Input
                            placeholder="ex: contacto@chefsynara.com"
                            value={form.reply_to}
                            onChange={handleChange("reply_to")}
                            data-testid="smtp-reply-to"
                        />
                    </div>

                    <div className="md:col-span-2 flex items-center justify-between bg-stone-50 ring-1 ring-stone-200 rounded-xl px-4 py-3">
                        <div>
                            <div className="font-medium text-stone-800">Ativar envio por SMTP</div>
                            <p className="text-sm text-stone-500">Quando ativo, todos os emails do site usam estas credenciais.</p>
                        </div>
                        <Switch
                            checked={form.enabled}
                            onCheckedChange={(v) => setForm((p) => ({ ...p, enabled: !!v }))}
                            data-testid="smtp-enabled"
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-2">
                    <Button onClick={save} disabled={saving} data-testid="smtp-save">
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Guardar definições
                    </Button>
                </div>
            </div>

            {/* Test send */}
            <div className="bg-white ring-1 ring-stone-200 rounded-2xl p-6 md:p-8">
                <header className="mb-4">
                    <h2 className="font-serif text-xl text-stone-900">Enviar email de teste</h2>
                    <p className="text-sm text-stone-500">Confirma se as credenciais estão a funcionar antes de receberes leads.</p>
                </header>
                <div className="grid md:grid-cols-[1fr_auto] gap-3">
                    <Input
                        type="email"
                        placeholder="email@destinatario.com"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        data-testid="smtp-test-to"
                    />
                    <Button
                        variant="outline"
                        onClick={sendTest}
                        disabled={testing || !form.enabled}
                        data-testid="smtp-test-send"
                    >
                        {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Enviar teste
                    </Button>
                </div>
                {!form.enabled && (
                    <p className="text-xs text-amber-700 mt-3">Ativa o SMTP e guarda as definições antes de fazer um teste.</p>
                )}
            </div>

            {/* Lead notification emails */}
            <NotificationsCard />
        </div>
    );
};

export default SettingsAdmin;
